import React, { useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { t } from '@/translations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contatti = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        language
      });
      toast.success(t('contact.success', language));
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error(t('contact.error', language));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contatti-page" className="pt-32">
      <section className="max-w-4xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <p className="text-xs tracking-[0.2em] uppercase text-primary font-semibold mb-4">
            {language === 'it' ? 'Parliamone' : 'Let\'s Talk'}
          </p>
          <h1
            className="text-5xl sm:text-6xl font-light tracking-tight leading-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
            data-testid="page-title"
          >
            {t('contact.title', language)}
          </h1>
          <p className="text-base leading-relaxed text-muted-foreground">
            {t('contact.subtitle', language)}
          </p>
        </motion.div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2
              className="text-3xl sm:text-4xl font-light tracking-tight leading-tight mb-8"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              {language === 'it' ? 'Info Contatto' : 'Contact Info'}
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-primary mb-2">
                  Email
                </h3>
                <a
                  href="mailto:info@matteogiacomettiph.it"
                  data-testid="contact-email"
                  className="text-foreground hover:text-primary transition-colors"
                >
                  info@matteogiacomettiph.it
                </a>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-primary mb-2">
                  WhatsApp
                </h3>
                <a
                  href="https://wa.me/393484512917"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="text-foreground hover:text-primary transition-colors"
                >
                  +39 348 451 2917
                </a>
              </div>

              <div>
                <h3 className="text-sm font-semibold uppercase tracking-widest text-primary mb-2">
                  Instagram
                </h3>
                <a
                  href="https://www.instagram.com/matteogiacometti_wedding"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram"
                  className="text-foreground hover:text-primary transition-colors"
                >
                  @matteogiacometti_wedding
                </a>
              </div>
            </div>

            <div className="mt-12">
              <img
                src="https://customer-assets-lqy194kg.emergentagent.net/job_remember-truly/artifacts/9b53doru_Eleven-7.webp"
                alt="Matteo Giacometti"
                className="w-full h-auto rounded-sm"
              />
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-6">
              <div>
                <Label htmlFor="name">{t('contact.name', language)}</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="input-name"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="email">{t('contact.email', language)}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="input-email"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone">{t('contact.phone', language)}</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="input-phone"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="service">{t('contact.service', language)}</Label>
                <Select onValueChange={handleServiceChange} value={formData.service}>
                  <SelectTrigger data-testid="select-service" className="mt-2">
                    <SelectValue placeholder={t('contact.selectService', language)} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="wedding" data-testid="service-wedding">
                      {t('contact.wedding', language)}
                    </SelectItem>
                    <SelectItem value="family" data-testid="service-family">
                      {t('contact.familyService', language)}
                    </SelectItem>
                    <SelectItem value="events" data-testid="service-events">
                      {t('contact.eventsService', language)}
                    </SelectItem>
                    <SelectItem value="portraits" data-testid="service-portraits">
                      {t('contact.portraitsService', language)}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message">{t('contact.message', language)}</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  data-testid="input-message"
                  className="mt-2"
                />
              </div>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                data-testid="submit-button"
                className="w-full"
              >
                {isSubmitting
                  ? (language === 'it' ? 'Invio...' : 'Sending...')
                  : t('contact.send', language)}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
